/* The ground the map is painted on (owner's ask, 2026-09-07: "less board game").

   The map used to paint every country its own pastel from a hue formula, one flat blue for the
   sea, and a black grid around every cell — a board, and one with 180 colours on it. Now a cell
   is *terrain*: one of seven biomes, decided by latitude and a seeded noise so the same cell is
   the same ground on every screen and every frame, painted as a flat, light colour. There is no
   grid: the ground is continuous regions, a country is a dashed line, the coast is a soft band,
   and the cell shows itself only where it matters — under the pointer, on the selection, along
   a route, and once the map is zoomed in far enough to carry coordinates (owner's pick of the
   three mock-ups, "C · Regions", 2026-09-07). A first cut painted 16-pixel tiles with a scatter
   of tones and was, in the owner's words, "too dark" and "a bit weird"; the snow tile — flat,
   light, nearly unmarked — was the one that looked right, so every biome is now painted the
   way the snow was.

   Nothing here reads game state. The world matrix (the active world's `matrix`, land cells keyed by
   "col-row", sea absent) decides land from sea, and `matrix[key]` — the country code —
   decides where a border goes. Everything else is arithmetic on the coordinates. */

import { activeWorld } from "src/utils/world"

/* The world is whichever one is active (`utils/world`, requirement 35): read at call time, so the
   same functions paint S, M and L. Every size's column count divides by both noise scales. */
const matrix = () => activeWorld().matrix

/* Biomes, one flat colour each. Light and a little desaturated on purpose: the ground is what a
   sprite and a route are read *against*, so it must never be the brightest thing on the map.
   No blue on land (blue is the sea and a human ally), no red (an enemy), and the greens sit
   away from the player-colour green. `city` is what a capital wears. */
export const BIOMES = {
  snow: "#e6ecf1",
  taiga: "#7ea56d",
  grass: "#9cc477",
  jungle: "#6da067",
  dry: "#d2c983",
  desert: "#e8d9a8",
  city: "#b1ada4",
}
export const SEA = "#4e8bbf"
export const SHALLOWS = "#79aed6" // the band of sea beside land
export const SURF = "#93c0e0" // the rim of that band, right at the shore
export const SHORE = "rgba(60, 54, 40, 0.35)" // the land side of the coast
export const BORDER_INK = "rgba(20, 24, 18, 0.5)" // a country's edge, dashed
export const GRID_INK = "rgba(14, 16, 12, 0.14)" // the cell grid, when it is shown at all
export const HOVER_INK = "rgba(255, 255, 255, 0.75)"

/* A small integer hash of two coordinates — deterministic, no state, spread enough that
   neighbouring cells do not share a pattern. Same shape as the star hash in Canvas.js. */
export const hash2 = (a, b) => {
  let h = (a * 73856093) ^ (b * 19349663)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return (h ^ (h >>> 16)) >>> 0
}

const unit = (a, b) => (hash2(a, b) % 10007) / 10007

/* Value noise, in [0, 1): a hash at every `scale` cells, smoothly interpolated between. It is
   what gives a latitude band patches — a forest in the plain, a desert that is not a stripe —
   rather than a hard line at row 30. Wraps east to west like the world — which needs `scale` to
   divide the column count, or the last patch is narrower than the rest and the seam shows. */
export const noiseAt = (col, row, scale = 8, seed = 0) => {
  const fx = col / scale
  const fy = row / scale
  const x0 = Math.floor(fx)
  const y0 = Math.floor(fy)
  const cellsAcross = Math.round(activeWorld().columns / scale)
  const wrap = (x) => ((x % cellsAcross) + cellsAcross) % cellsAcross
  const tx = fx - x0
  const ty = fy - y0
  const smooth = (t) => t * t * (3 - 2 * t)
  const sx = smooth(tx)
  const sy = smooth(ty)
  const n00 = unit(wrap(x0) + seed * 1000, y0)
  const n10 = unit(wrap(x0 + 1) + seed * 1000, y0)
  const n01 = unit(wrap(x0) + seed * 1000, y0 + 1)
  const n11 = unit(wrap(x0 + 1) + seed * 1000, y0 + 1)
  const top = n00 + (n10 - n00) * sx
  const bottom = n01 + (n11 - n01) * sx
  return top + (bottom - top) * sy
}

/* Degrees of latitude a row sits at: row 0 is the pole, the middle row the equator — over the
   map's own rows, so the snow and the greens fall in the same bands on every size. The world
   matrix is an equirectangular map, so this is exact, not a guess. */
export const latitudeOf = (row) => 90 - (row + 0.5) * (180 / activeWorld().rows)

export const isLand = (col, row) => matrix()[`${col}-${row}`] !== undefined

/* Which ground a land cell is. Latitude picks the band, the noise picks the exception — a
   patch of forest in the plain, a stretch of scrub in the desert — so the seam between two bands
   is a ragged edge and not a line across the whole world. Pure: same cell, same answer.
   `n` is fine-grained (patches of forest), `m` coarse (whole dry regions); the desert is an
   exception inside the dry belt rather than the belt itself, or the tropics read as one
   sand-coloured stripe across every continent. */
export const biomeAt = (col, row) => {
  const lat = Math.abs(latitudeOf(row))
  const n = noiseAt(col, row, 8, 1)
  const m = noiseAt(col, row, 20, 2)
  if (lat >= 68) return "snow"
  if (lat >= 58) return n > 0.62 ? "snow" : "taiga"
  if (lat >= 46) return n > 0.55 ? "taiga" : "grass"
  if (lat >= 34) return n > 0.78 ? "taiga" : m > 0.6 ? "dry" : "grass"
  if (lat >= 22) return m > 0.62 ? "desert" : n < 0.3 ? "grass" : "dry"
  if (lat >= 12) return m > 0.7 ? "desert" : n > 0.55 ? "jungle" : "dry"
  return m > 0.72 ? "dry" : "jungle"
}

const wrapCol = (col) => {
  const { columns } = activeWorld()
  return ((col % columns) + columns) % columns
}

/* The four neighbours of a cell that are land, N E S W, wrapping east to west. */
export const landNeighbours = (col, row) => [
  isLand(col, row - 1),
  isLand(wrapCol(col + 1), row),
  isLand(col, row + 1),
  isLand(wrapCol(col - 1), row),
]

/* Which of a land cell's east and south edges are a border with another country. Each edge is
   drawn once, from the cell on its north-west side, so the loop over visible cells draws every
   border exactly once. */
export const bordersAt = (col, row) => {
  const world = matrix()
  const code = world[`${col}-${row}`]
  if (code === undefined) return [false, false]
  const east = world[`${wrapCol(col + 1)}-${row}`]
  const south = world[`${col}-${row + 1}`]
  return [east !== undefined && east !== code, south !== undefined && south !== code]
}

/* A band along each of a cell's four edges that a predicate picks, `depth` pixels deep. The
   coast is three of these: shallows and surf on the sea side, shore on the land side. */
const bands = (ctx, x, y, w, h, depth, sides, colour) => {
  const [n, e, s, west] = sides
  ctx.fillStyle = colour
  if (n) ctx.fillRect(x, y, w, depth)
  if (s) ctx.fillRect(x, y + h - depth, w, depth)
  if (west) ctx.fillRect(x, y, depth, h)
  if (e) ctx.fillRect(x + w - depth, y, depth, h)
}

/* Paint one cell's ground at (x, y), `w` by `h` pixels, coast included. `capital` swaps the
   biome for the city colour. The bands scale with the cell so a coast is the same coast at
   every zoom rather than a hairline zoomed out and a bar zoomed in.

   The box is the caller's, snapped to device pixels — a cell whose edges land between pixels
   is drawn with antialiased edges, and two such cells side by side leave a faint seam where
   neither quite covers the pixel they share, which on a plain ground read as a grid the map is
   not supposed to have. */
export const drawGround = (ctx, { x, y, w, h, col, row, capital = false }) => {
  const land = isLand(col, row)
  const size = Math.min(w, h)
  ctx.fillStyle = land ? BIOMES[capital ? "city" : biomeAt(col, row)] : SEA
  ctx.fillRect(x, y, w, h)
  const neighbours = landNeighbours(col, row)
  if (land) {
    const sea = neighbours.map((isLandThere) => !isLandThere)
    bands(ctx, x, y, w, h, Math.max(1, size * 0.07), sea, SHORE)
  } else {
    bands(ctx, x, y, w, h, Math.max(2, size * 0.22), neighbours, SHALLOWS)
    bands(ctx, x, y, w, h, Math.max(1, size * 0.07), neighbours, SURF)
  }
}

/* The lines over the ground: a country's border, dashed, on the east and south edges that need
   one — and, only when asked, the grid. The grid is asked for only once the map is zoomed in far
   enough to carry coordinates (`Canvas.js`), which is the one time a player is counting cells
   rather than reading the map. */
export const drawEdges = (ctx, { x, y, w, h, col, row, grid }) => {
  const land = isLand(col, row)
  if (grid && land) {
    ctx.fillStyle = GRID_INK
    ctx.fillRect(x + w - 1, y, 1, h)
    ctx.fillRect(x, y + h - 1, w, 1)
  }
  if (!land) return
  const [east, south] = bordersAt(col, row)
  if (!east && !south) return
  const size = Math.min(w, h)
  ctx.save()
  ctx.strokeStyle = BORDER_INK
  ctx.lineWidth = 1
  ctx.setLineDash([Math.max(3, size * 0.15), Math.max(2, size * 0.1)])
  ctx.beginPath()
  if (east) {
    ctx.moveTo(x + w - 0.5, y)
    ctx.lineTo(x + w - 0.5, y + h)
  }
  if (south) {
    ctx.moveTo(x, y + h - 0.5)
    ctx.lineTo(x + w, y + h - 0.5)
  }
  ctx.stroke()
  ctx.restore()
}

/* The cell under the pointer: a thin light frame, which with no grid is the only thing that
   says where one cell ends and the next begins. */
export const drawHover = (ctx, { x, y, w, h }) => {
  ctx.lineWidth = 1
  ctx.strokeStyle = HOVER_INK
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1)
}
